import {
  Footer,
  Imprint,
  Privacy,
  SalesPartnerSettings,
  ServiceCenter,
  Sidebar,
  TermsOfService,
  UserManagement,
  imprint,
  infoCenter,
  marketLocation,
  marketLocationMasterData,
  marketLocationRevenueReport,
  optimizationDownload,
  privacy,
  root,
  salesPartnerSettings,
  termsOfService,
  userManagement,
} from '@e2m-customer-portal/core';
import { ForecastPage, MasterDataPage, OptimizationDownload, RevenueReportPage } from '@e2m-customer-portal/domains';
import {
  useFeatureFlags,
  useMarketLocationId,
  useOptimizationStatus,
  usePermissions,
  useSalesPartner,
  useSalesPartners,
  useSnackbar,
  useTitle,
  useTokenInfo,
} from '@e2m-customer-portal/hooks';
import { PermissionId, UserRole } from '@e2m-customer-portal/shared';
import {
  ErrorComponent,
  ErrorDialog,
  Header,
  HeaderUserSettings,
  JammerBanner,
  Loading,
  ScrollToTop,
} from '@e2m-customer-portal/ui';
import { Alert, Snackbar } from '@mui/material';
import React, { Suspense, useCallback, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Navigate, Route, Routes } from 'react-router';
import { useToggle } from 'react-use';
import './i18n';
import { isGranted as checkGranted } from './utils';

export const App: React.FC = () => {
  document.title = useTitle().appTitle;
  const [sidebarOpen, toggleSidebar] = useToggle(false);
  const [sidebarIsExpanded, setSidebarIsExpanded] = useState(true);
  const { open: snackbarOpen, severity, message, close } = useSnackbar();

  const { isOptimizationEnabled, useNewSalesPartnerAPI } = useFeatureFlags();

  const { roles } = useTokenInfo();
  const salesPartner = !useNewSalesPartnerAPI ? useSalesPartner() : undefined;
  // Only check sales partners for plant operators to prevent 'unauthorized' errors in backend
  const salesPartners = useNewSalesPartnerAPI && roles.has(UserRole.PlantOperator) ? useSalesPartners() : [];
  const { permissions, loading } = usePermissions();
  const optimizationStatus = useOptimizationStatus();
  const marketLocationId = useMarketLocationId();

  const isGranted = checkGranted(permissions);

  const onMenuClick = useCallback(() => {
    toggleSidebar();
  }, [toggleSidebar]);

  const [sidebarOffset, setSidebarOffset] = useState(0);

  return (
    <div data-testid="loaded" className="App flex flex-col min-h-screen motion-reduce:!transition-none">
      <ScrollToTop />
      <Snackbar
        open={snackbarOpen && severity !== 'error'}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        onClose={close}
      >
        <Alert severity={severity}>{message instanceof Error ? message.message : message}</Alert>
      </Snackbar>
      <ErrorDialog open={snackbarOpen && severity === 'error'} error={message} onClose={close} />

      <Header
        onMenuClick={onMenuClick}
        sidebarIsExpanded={sidebarIsExpanded}
        onMaintenanceBannerHeightChange={setSidebarOffset}
      >
        <HeaderUserSettings />
      </Header>

      {salesPartner && <JammerBanner salesPartners={[salesPartner]} open={!salesPartner.isConfigured} />}
      {!!salesPartners?.length && (
        <JammerBanner
          salesPartners={salesPartners.filter((sp) => !sp.isConfigured)}
          open={salesPartners.some((sp) => !sp.isConfigured)}
        />
      )}

      <div className="flex grow">
        <ErrorBoundary FallbackComponent={ErrorComponent}>
          <Sidebar
            open={sidebarOpen}
            onClose={toggleSidebar}
            sidebarOffset={sidebarOffset}
            onExpansionChange={setSidebarIsExpanded}
          />
        </ErrorBoundary>

        <main className="w-full flex flex-col grow">
          <ErrorBoundary FallbackComponent={ErrorComponent}>
            {loading ? (
              <Loading />
            ) : (
              <Routes>
                <Route
                  path={marketLocationRevenueReport.pattern}
                  element={
                    <Suspense fallback={<Loading />}>
                      <RevenueReportPage sidebarOffset={sidebarOffset} />
                    </Suspense>
                  }
                />

                <Route path={marketLocationMasterData.pattern} element={<MasterDataPage />} />

                <Route path={marketLocation.pattern} element={<ForecastPage sidebarOffset={sidebarOffset} />} />

                <Route path={imprint.pattern} element={<Imprint />} />

                <Route path={termsOfService.pattern} element={<TermsOfService />} />

                <Route path={privacy.pattern} element={<Privacy />} />

                <Route path={infoCenter.pattern} element={<ServiceCenter />} />

                {isOptimizationEnabled && optimizationStatus?.isAuthorized && (
                  <Route path={optimizationDownload.pattern} element={<OptimizationDownload />} />
                )}

                {(isGranted(PermissionId.configureUsers) || isGranted(PermissionId.configureAdmins)) && (
                  <Route path={userManagement.pattern} element={<UserManagement />} />
                )}

                {salesPartner && (
                  <Route
                    path={salesPartnerSettings.pattern}
                    element={<SalesPartnerSettings salesPartners={[salesPartner]} />}
                  />
                )}

                {!!salesPartners?.length && (
                  <Route
                    path={salesPartnerSettings.pattern}
                    element={<SalesPartnerSettings salesPartners={salesPartners} />}
                  />
                )}

                <Route path={root.pattern} element={<Loading />} />

                {/* Temporary redirects */}
                <Route path="/impressum" element={<Navigate to={imprint.getLink()} replace />} />
                <Route path="/datenschutz" element={<Navigate to={privacy.getLink()} replace />} />
                <Route path="/nutzungsbedingungen" element={<Navigate to={termsOfService.getLink()} replace />} />
                <Route path="/optimierung/*" element={<Navigate to={optimizationDownload.getLink()} replace />} />
                <Route path="/vertriebspartner" element={<Navigate to={salesPartnerSettings.getLink()} replace />} />
                <Route path="/benutzerverwaltung" element={<Navigate to={userManagement.getLink()} replace />} />

                {!marketLocationId && <Route path="*" element={<Navigate to={root.getLink()} replace />} />}
              </Routes>
            )}
          </ErrorBoundary>
          <Footer />
        </main>
      </div>
    </div>
  );
};
